import React, { ChangeEvent, useState, useEffect, useCallback } from 'react';
import { DownOutlined, PushpinOutlined } from '@ant-design/icons';
import { Button, Input, Menu, Checkbox, Dropdown, Divider, Space, message } from 'antd';
import commonSelector from '@store/common/selectors';
import featureNames from '@utils/featureToggle/constants';
import { AnchorIcon, ItemContent, MetricSelector, StyledNoData } from './styled';
import { useSelector } from 'react-redux';
import type { CheckboxChangeEvent } from 'antd/es/checkbox';

const { Search } = Input;

const CustomizeDropdown = ({
    allMetrics,
    maxAnchorCount,
    applyAnchorMetrics,
    selectedMetrics,
    anchoredMetrics,
    disabled,
    containerRef,
    applySelectedMetrics,
    mandatoryDimensions,
}) => {
    const [innerSelectedMetrics, changeInnerSelectedMetrics] = useState([]);
    const [innerAllMetrics, changeInnerAllMetrics] = useState({});
    const [searchText, setSearchText] = useState('');
    const [open, setOpenChange] = useState(false);

    useEffect(() => {
        changeInnerSelectedMetrics(selectedMetrics);
    }, [selectedMetrics]);

    useEffect(() => {
        changeInnerAllMetrics(allMetrics);
    }, [allMetrics]);

    const { features } = useSelector(commonSelector);
    const timelineFlag = features[featureNames.VIEWER_DRILLDOWN_TIMELINE];

    const onChangeSearch = (e: ChangeEvent<HTMLInputElement>) => {
        const searchText = e.target.value;
        setSearchText(searchText);
        let searchedMetrics = {};
        Object.entries(allMetrics).filter((entry: any) => {
            const filteredEntries = entry[1].filter(e => {
                if (e.name.toLowerCase().indexOf(searchText.toLowerCase()) >= 0) {
                    return e
                }
            });
            if (filteredEntries.length > 0) {
                searchedMetrics = Object.assign(searchedMetrics, { [entry[0]]: filteredEntries });
            }
        });
        changeInnerAllMetrics(searchedMetrics);
    }

    const onChange = (e: CheckboxChangeEvent, metric) => {
        e.stopPropagation();
        let tempMetricCopy: any = innerSelectedMetrics;
        if (e.target.checked) {
            if (tempMetricCopy.indexOf(metric) === -1) {
                tempMetricCopy = [...tempMetricCopy, metric]
            }
            changeInnerSelectedMetrics(tempMetricCopy);
        } else {
            delete anchoredMetrics[metric];
            tempMetricCopy = tempMetricCopy.filter((item) => item !== metric);
            changeInnerSelectedMetrics(tempMetricCopy)
        }
    };

    const resetSearch = () => {
        setSearchText('');
        changeInnerAllMetrics(allMetrics);
    };

    useEffect(() => {
        if (!open) {
            resetSearch();
            Object.keys(anchoredMetrics).map((metricKey) => {
                if (!selectedMetrics.includes(metricKey)) {
                    delete anchoredMetrics[metricKey];
                }
            });
            changeInnerSelectedMetrics(selectedMetrics);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open]);

    const onApplyMetrics = useCallback(() => {
        if (innerSelectedMetrics.length < 1) {
            message.warning(`Please select atleast one dimension/metric.`);
            return;
        }
        applySelectedMetrics(innerSelectedMetrics);
        setOpenChange(false);
    }, [applySelectedMetrics, innerSelectedMetrics]);

    const onClearMetrics = () => {
        changeInnerSelectedMetrics(mandatoryDimensions);
    };

    const renderMenus = () => {
        const keys = Object.keys(innerAllMetrics);
        const menuGroups = keys.map((key) => {
            const groupItems = innerAllMetrics[key].filter((v) => {
                // If user does not have timeline permission, we should hidden it on dropmenu
                if (v.metric === 'SessionId' && !timelineFlag) {
                    return false;
                }
                return true;
            }).map((item) => {
                const isSelected = innerSelectedMetrics.some(m => m === item.metric);
                const isAnchorApplied = innerSelectedMetrics.some(m => m === item.metric);
                return (
                    <div
                        key={item.metric}
                        className="dimension-wrapper"
                    >
                        <Checkbox onChange={(e) => onChange(e, item.metric)} checked={isSelected} disabled={mandatoryDimensions.includes(item.metric)}>
                            <ItemContent title={item.name}>{item.name}</ItemContent>
                        </Checkbox>
                        {isAnchorApplied && (
                            <AnchorIcon
                                component={PushpinOutlined as React.ForwardRefExoticComponent<any>}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    const anchoredMetricsCount = Object.keys(anchoredMetrics)
                                        .filter(metricKey => anchoredMetrics[metricKey]).length;
                                    if (anchoredMetricsCount === maxAnchorCount && !anchoredMetrics[item.metric]) {
                                        message.warning(`Cannot pin more than ${maxAnchorCount} columns`);
                                        return;
                                    }
                                    applyAnchorMetrics(item.metric);
                                }}
                                rotate={anchoredMetrics[item.metric] ? -45 : 0}
                            />)
                        }
                    </div>
                );
            });
            return (
                <Menu.ItemGroup key={key} title={key} >
                    {groupItems}
                </Menu.ItemGroup>
            );
        });

        return (
            <MetricSelector>
                <Search
                    placeholder="Search Dimension / Metrics"
                    value={searchText}
                    onChange={onChangeSearch}
                    allowClear
                />
                {Object.keys(innerAllMetrics).length > 0 ?
                    <Menu
                        className="menu-ul"
                        selectable={false}
                    >
                        {menuGroups}
                    </Menu> : <StyledNoData>No dimensions Available.</StyledNoData>}
                <Divider style={{ margin: 0 }} />
                <Space className="bottom-space">
                    <Button type="primary" size="small" ghost onClick={() => onClearMetrics()}>Clear All</Button>
                    <Button type="primary" size="small" onClick={() => onApplyMetrics()}>Apply</Button>
                </Space>
            </MetricSelector >
        );
    };

    return (
        <Dropdown
            dropdownRender={() => renderMenus()}
            trigger={['click']}
            getPopupContainer={() => containerRef.current}
            open={open}
            onOpenChange={setOpenChange}
            disabled={disabled}
        >
            <Button
                id="metrics-dropdown-multi-select"
                disabled={disabled}
            >
                Customize
                <DownOutlined />
            </Button>
        </Dropdown>
    );
};



export default CustomizeDropdown;
